import { ref, watch } from "vue";

import type { ContentfulItem } from "@tmi/types/entities";
import { adoptUnscopedValue, ownerScopedKey, storageOwner } from "@/utils/storage-owner";

/*****************************************************************************
 * "Seen in the bell" state for recent content.
 *
 * Distinct from read state (content-read-state.ts):
 * - read    = the user has opened the content (persisted, drives many UIs,
 *             including the NEW pill on content cards)
 * - acked   = the item was listed when the user opened the notifications
 *             bell. It stops counting towards the bell badge and the other
 *             unread counters, but the row stays in the bell list and the
 *             content keeps its NEW pill until it is actually opened.
 *
 * Acknowledgement used to happen on hover (a bell row or a content card), so
 * moving the mouse down a list or scrolling one under the pointer cleared
 * badges the member had never looked at (gap_alert_delivery_and_notifications-4).
 *
 * Persisted to localStorage as { key: ackedAt } so entries can be pruned; the
 * original format was a bare array of keys that only ever grew. Kept per
 * member on this browser (UX roadmap #63): the next account on a shared device
 * used to inherit it, and it survives the member's own sign-out.
 *****************************************************************************/

/** The base of the localStorage key; a member's state lives under `base:<owner>` (utils/storage-owner.ts). */
export const NOTIFICATION_ACK_STORAGE_KEY = 'tmi:notifications-acknowledged';

/** The key the signed-in member's acknowledgements live under; the first read for a member adopts the unscoped value. */
function storageKey(): string {
    const owner: string | null = storageOwner.value;
    adoptUnscopedValue( NOTIFICATION_ACK_STORAGE_KEY, owner );
    return ownerScopedKey( NOTIFICATION_ACK_STORAGE_KEY, owner );
}

// Longer than any item stays new in content-read-state.ts (5 days, or up to 21 for an upcoming webinar): content is
// acknowledged after it is published, so an acknowledgement older than this belongs to content no counter looks at
// any more. At 7 days an acknowledged upcoming webinar came back onto the badge before it started.
export const NOTIFICATION_ACK_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

type AcknowledgementState = Record< string, number >;

function prune( state: AcknowledgementState, now: number ): AcknowledgementState {
    const next: AcknowledgementState = {};
    Object.entries( state ).forEach( ( [ key, ackedAt ] ) => {
        if ( now - ackedAt <= NOTIFICATION_ACK_RETENTION_MS ) {
            next[ key ] = ackedAt;
        }
    } );
    return next;
}

function save( state: AcknowledgementState ): void {
    try {
        globalThis.localStorage?.setItem( storageKey(), JSON.stringify( state ) );
    } catch {
        // localStorage unavailable (private mode, quota, SSR) — degrade silently
    }
}

function load( now = Date.now() ): AcknowledgementState {
    let parsed: unknown;
    try {
        const raw = globalThis.localStorage?.getItem( storageKey() );
        if ( !raw ) return {};
        parsed = JSON.parse( raw );
    } catch {
        return {};
    }

    const state: AcknowledgementState = {};
    let migrated = false;
    if ( Array.isArray( parsed ) ) {
        // Legacy string[]: no timestamps were kept, so stamp them now and let them age out.
        parsed.forEach( ( key: unknown ) => {
            if ( typeof key === 'string' ) state[ key ] = now;
        } );
        migrated = true;
    } else if ( parsed && typeof parsed === 'object' ) {
        Object.entries( parsed as Record< string, unknown > ).forEach( ( [ key, ackedAt ] ) => {
            if ( typeof ackedAt === 'number' && Number.isFinite( ackedAt ) ) {
                state[ key ] = ackedAt;
            } else {
                migrated = true;
            }
        } );
    }

    const pruned = prune( state, now );
    if ( migrated || Object.keys( pruned ).length !== Object.keys( state ).length ) {
        save( pruned );
    }
    return pruned;
}

const acknowledged = ref< AcknowledgementState >( load() );

// UX roadmap #63: another member signed in, so their acknowledgements apply.
watch( storageOwner, () => {
    acknowledged.value = load();
} );

// UX roadmap #63: the bell opened in another tab clears the badges here too. Loaded once at import, this tab kept
// counting what the member had already seen until a reload.
if ( typeof window !== 'undefined' ) {
    window.addEventListener( 'storage', ( event: StorageEvent ) => {
        if ( null === event.key || ownerScopedKey( NOTIFICATION_ACK_STORAGE_KEY, storageOwner.value ) === event.key ) {
            acknowledged.value = load();
        }
    } );
}

export function acknowledgementKey( content: ContentfulItem | undefined, contentType: string | undefined ): string | undefined {
    if ( !contentType ) return undefined;
    const id = content?.contentfulId ?? content?.slug;
    if ( !id ) return undefined;
    return `${ contentType }:${ id }`;
}

export type AcknowledgementEntry = {
    content: ContentfulItem | undefined,
    contentType: string | undefined,
};

export function useNotificationAcknowledgement() {
    function isAcknowledged( content: ContentfulItem | undefined, contentType: string | undefined ): boolean {
        const key = acknowledgementKey( content, contentType );
        return !!key && key in acknowledged.value;
    }

    /** Acknowledges every entry in one write. */
    function acknowledgeAll( entries: AcknowledgementEntry[] ): void {
        const now = Date.now();
        const keys = entries
            .map( ( { content, contentType } ) => acknowledgementKey( content, contentType ) )
            .filter( ( key ): key is string => !!key && !( key in acknowledged.value ) );
        if ( keys.length === 0 ) return;

        const next = prune( acknowledged.value, now );
        keys.forEach( ( key ) => { next[ key ] = now; } );
        acknowledged.value = next;
        save( next );
    }

    function acknowledge( content: ContentfulItem | undefined, contentType: string | undefined ): void {
        acknowledgeAll( [ { content, contentType } ] );
    }

    return {
        acknowledge,
        acknowledgeAll,
        isAcknowledged,
    };
}
